import { useApolloClient, useMutation } from '@apollo/client/react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { GetCurrentUserDocument, SiteRole, UserLoginDocument } from '../gql/graphql'
import { RoleProvider, type SiteInfo } from '../hooks'
import RoleSelectionDialog, { type SelectedRole } from './RoleSelectionDialog'

interface LoginRedirectorProps {
  renderLoginScreen: (
    onLoginSuccess: (
      sites: SiteInfo[],
      role: SelectedRole,
      isAppAdmin: boolean,
    ) => void,
  ) => ReactNode
  renderApp: (role: SelectedRole) => ReactNode
}

type LoginState =
  | { type: 'initial' }
  | { type: 'qrlogin' }
  | { type: 'ready'; sites: SiteInfo[]; role: SelectedRole; isAppAdmin: boolean }
  | { type: 'error' }

function resolveRoleFromLocalStorage(
  sites: SiteInfo[],
  isAppAdmin: boolean,
): SelectedRole {
  const adminSites = sites.filter(
    (s) => s.role === SiteRole.Admin || s.role === SiteRole.SuperAdmin,
  )
  const saved = localStorage.getItem('selectedRole')

  if (saved === 'app_admin' && isAppAdmin) return { type: 'app_admin' }
  if (saved === 'site_admin') {
    const siteId = localStorage.getItem('currentSiteId')
    const site = adminSites.find((s) => s.id === siteId)
    if (site) return { type: 'site_admin', site }
  }

  if (isAppAdmin) return { type: 'app_admin' }
  if (adminSites.length > 0) return { type: 'site_admin', site: adminSites[0] }
  return { type: 'site_admin', site: sites[0] ?? { id: '', name: '', role: 'member' } }
}

/**
 * Renders the app only after ensuring the user is logged in.
 *
 * If no saved login information is found, it shows `renderLoginScreen`'s QR code
 * login screen. Once logged in, calls `renderApp` with the selected role so the
 * caller can decide which app to render.
 */
export default function LoginRedirector({
  renderLoginScreen,
  renderApp,
}: LoginRedirectorProps) {
  const apolloClient = useApolloClient()
  const [state, setState] = useState<LoginState>({ type: 'initial' })
  const [login] = useMutation(UserLoginDocument)

  useEffect(() => {
    const authToken = localStorage.getItem('authToken')

    if (authToken) {
      ;(async () => {
        try {
          const { data } = await login()

          if (!data?.login || data.login.__typename === 'GraphqlRequestError') {
            const errorMsg =
              data?.login?.__typename === 'GraphqlRequestError'
                ? data.login.message
                : 'No data received'
            console.error('Login failed:', errorMsg)
            localStorage.removeItem('authToken')
            setState({ type: 'qrlogin' })
            return
          }

          const loginResponse = data.login
          localStorage.setItem('authToken', loginResponse.authToken)
          const sites = loginResponse.user.sites as SiteInfo[]
          const isAppAdmin = loginResponse.user.isAppAdmin
          const role = resolveRoleFromLocalStorage(sites, isAppAdmin)

          apolloClient.writeQuery({
            query: GetCurrentUserDocument,
            data: {
              __typename: 'Query',
              // biome-ignore lint/suspicious/noExplicitAny: writeQuery type doesn't accept UserBasic
              currentUser: loginResponse.user as any,
            },
          })
          setState({ type: 'ready', sites, role, isAppAdmin })
        } catch (error) {
          console.error('Login error:', error)
          localStorage.removeItem('authToken')
          setState({ type: 'qrlogin' })
        }
      })()
    } else {
      setState({ type: 'qrlogin' })
    }
  }, [login, apolloClient])

  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false)

  const onLoginSuccess = useCallback(
    (sites: SiteInfo[], role: SelectedRole, isAppAdmin: boolean) => {
      setState({ type: 'ready', sites, role, isAppAdmin })
    },
    [],
  )

  const handleRoleSelected = useCallback((role: SelectedRole) => {
    if (role.type === 'site_admin') {
      localStorage.setItem('selectedRole', 'site_admin')
      localStorage.setItem('currentSiteId', role.site.id)
    } else {
      localStorage.setItem('selectedRole', 'app_admin')
    }
    setState((prev) => (prev.type === 'ready' ? { ...prev, role } : prev))
    setShowRoleSwitcher(false)
    // Navigate to home to avoid 404 when switching between apps with different routes
    if (window.location.pathname !== '/') {
      window.location.pathname = '/'
    }
  }, [])

  if (state.type === 'initial') return null
  if (state.type === 'error') return <div>无法登陆</div>
  if (state.type === 'qrlogin') return renderLoginScreen(onLoginSuccess)

  const { sites, role, isAppAdmin } = state
  const adminSites = sites.filter(
    (s) => s.role === SiteRole.Admin || s.role === SiteRole.SuperAdmin,
  )
  const canSwitchRole = isAppAdmin ? adminSites.length > 0 : adminSites.length > 1

  return (
    <>
      <RoleProvider
        sites={sites}
        isAppAdmin={isAppAdmin}
        selectedRole={role}
        onSwitchRole={canSwitchRole ? () => setShowRoleSwitcher(true) : () => {}}
      >
        {/* Key forces full remount when role changes, clearing router state */}
        <div key={role.type === 'app_admin' ? 'app-admin' : `site-${role.site.id}`}>
          {renderApp(role)}
        </div>
      </RoleProvider>
      {showRoleSwitcher && (
        <RoleSelectionDialog
          isAppAdmin={isAppAdmin}
          adminSites={adminSites}
          currentRole={role}
          onSelect={handleRoleSelected}
          onClose={() => setShowRoleSwitcher(false)}
        />
      )}
    </>
  )
}
